import React, { useState, useEffect } from 'react';
import { TablutPiece, Position } from '../types/game';

interface TablutBoardProps {
  board: (TablutPiece | null)[][];
  selectedPiece?: Position;
  possibleMoves?: Position[];
  lastMove?: Position;
  kingPosition?: Position;
  onSelectPosition: (row: number, col: number) => void;
  isAIThinking?: boolean;
}

const TablutBoard: React.FC<TablutBoardProps> = ({
  board,
  selectedPiece,
  possibleMoves = [],
  lastMove,
  kingPosition,
  onSelectPosition,
  isAIThinking = false
}) => {
  // 响应式状态管理
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window !== 'undefined') {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      };
    }
    return { width: 1024, height: 768 };
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // 特殊位置
  const throne: Position = { row: 4, col: 4 }; // 王座位置
  const corners: Position[] = [
    { row: 0, col: 0 }, { row: 0, col: 8 },
    { row: 8, col: 0 }, { row: 8, col: 8 }
  ]; // 四个角落

  // 检查是否为特殊位置
  const isSpecialPosition = (row: number, col: number): { isThrone: boolean; isCorner: boolean } => {
    return {
      isThrone: row === throne.row && col === throne.col,
      isCorner: corners.some(corner => corner.row === row && corner.col === col)
    };
  };

  // 检查是否为国王位置
  const isKingPosition = (row: number, col: number): boolean => {
    return kingPosition?.row === row && kingPosition?.col === col;
  };

  // 检查是否为最后移动位置
  const isLastMove = (row: number, col: number): boolean => {
    return lastMove?.row === row && lastMove?.col === col;
  };

  // 检查是否为选中的棋子
  const isSelectedPiece = (row: number, col: number): boolean => {
    return selectedPiece?.row === row && selectedPiece?.col === col;
  };

  // 检查是否为可能的移动位置
  const isPossibleMove = (row: number, col: number): boolean => {
    return possibleMoves.some(pos => pos.row === row && pos.col === col);
  };

  // 计算棋盘坐标转换（响应式设计）
  const getBoardCoordinates = (row: number, col: number) => {
    const cellSize = windowSize.width < 640 ? 35 :  // 移动端
                    windowSize.width < 1024 ? 45 : // 平板
                    65;                           // 桌面端
    const padding = windowSize.width < 640 ? 25 :   // 移动端
                    windowSize.width < 1024 ? 30 :  // 平板
                    50;                           // 桌面端

    return {
      x: padding + col * cellSize,
      y: padding + row * cellSize
    };
  };

  // 计算SVG尺寸（响应式）
  const getSvgSize = () => {
    if (windowSize.width < 640) {
      return { width: 350, height: 350 }; // 移动端
    } else if (windowSize.width < 1024) {
      return { width: 450, height: 450 }; // 平板
    }
    return { width: 650, height: 650 };     // 桌面端
  };

  const svgSize = getSvgSize();

  // 计算棋子大小（响应式）
  const getPieceSize = () => {
    if (windowSize.width < 640) {
      return 15;  // 手机端
    }
    return 28;     // 电脑端
  };

  // 计算棋子阴影大小
  const getShadowSize = () => {
    if (windowSize.width < 640) {
      return 2;   // 手机端
    }
    return 3;     // 电脑端
  };

  // 计算文字大小
  const getTextSize = () => {
    if (windowSize.width < 640) {
      return 10;  // 手机端
    }
    return 28;   // 电脑端
  };

  const pieceSize = getPieceSize();
  const shadowSize = getShadowSize();
  const textSize = getTextSize();

  return (
    <div className="flex items-center justify-center w-full h-full">
      <div className="relative bg-amber-50 rounded-xl shadow-2xl border-4 border-amber-700 p-3 w-full max-w-[700px] aspect-square">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block' }}
        >
          {/* 绘制棋盘网格线 */}
          <g className="opacity-80">
            {/* 垂直线 */}
            {Array.from({ length: 9 }, (_, i) => {
              const coords = getBoardCoordinates(0, i);
              return (
                <line
                  key={`v-${i}`}
                  x1={coords.x}
                  y1={coords.y}
                  x2={coords.x}
                  y2={getBoardCoordinates(8, i).y}
                  stroke="#8B4513"
                  strokeWidth={windowSize.width < 640 ? 1 : 2}
                />
              );
            })}

            {/* 水平线 */}
            {Array.from({ length: 9 }, (_, i) => {
              const coords = getBoardCoordinates(i, 0);
              return (
                <line
                  key={`h-${i}`}
                  x1={coords.x}
                  y1={coords.y}
                  x2={getBoardCoordinates(i, 8).x}
                  y2={coords.y}
                  stroke="#8B4513"
                  strokeWidth={windowSize.width < 640 ? 1 : 2}
                />
              );
            })}
          </g>

          {/* 绘制特殊位置标记 */}
          {/* 王座 */}
          {(() => {
            const throneCoords = getBoardCoordinates(throne.row, throne.col);
            return (
              <g>
                <circle
                  cx={throneCoords.x}
                  cy={throneCoords.y}
                  r={pieceSize-2}
                  fill="#8B4513"
                  fillOpacity={0.95}
                  stroke="#654321"
                  strokeWidth={2}
                />
                <text
                  x={throneCoords.x}
                  y={throneCoords.y + 6}
                  textAnchor="middle"
                  fontSize={pieceSize}
                  fill="#654321"
                  fontWeight="bold"
                >
                  🏰
                </text>
              </g>
            );
          })()}

          {/* 四个角落（逃脱点） */}
          {corners.map((corner, index) => {
            const cornerCoords = getBoardCoordinates(corner.row, corner.col);
            return (
              <g key={`corner-${index}`}>
                <circle
                  cx={cornerCoords.x}
                  cy={cornerCoords.y}
                  r={pieceSize}
                  fill="#FFD700"
                  fillOpacity={0.9}
                  stroke="#DAA520"
                  strokeWidth={2}
                  strokeDasharray="4,2"
                />
                <text
                  x={cornerCoords.x}
                  y={cornerCoords.y + 5}
                  textAnchor="middle"
                  fontSize={textSize + 5}
                  fill="#DAA520"
                  fontWeight="bold"
                >
                  &nbsp;
                </text>
              </g>
            );
          })}

          {/* 绘制棋子和格子 */}
          {board.map((row, rowIndex) =>
            row.map((piece, colIndex) => {
              const coords = getBoardCoordinates(rowIndex, colIndex);
              const selected = isSelectedPiece(rowIndex, colIndex);
              const possibleMove = isPossibleMove(rowIndex, colIndex);
              const lastMovePos = isLastMove(rowIndex, colIndex);
              const kingPos = isKingPosition(rowIndex, colIndex);
              const special = isSpecialPosition(rowIndex, colIndex);

              return (
                <g key={`${rowIndex}-${colIndex}`}>
                  {/* 格子背景高亮 */}
                  {selected && (
                    <circle
                      cx={coords.x}
                      cy={coords.y}
                      r={pieceSize + 4}
                      fill="#FFD700"
                      fillOpacity={0.5}
                      stroke="#FFA500"
                      strokeWidth={windowSize.width < 640 ? 2 : 3}
                    />
                  )}

                  {lastMovePos && (
                    <circle
                      cx={coords.x}
                      cy={coords.y}
                      r={pieceSize + 4}
                      fill="#90EE90"
                      fillOpacity={0.5}
                      stroke="#228B22"
                      strokeWidth={windowSize.width < 640 ? 1 : 2}
                    />
                  )}

                  {/* 可能移动位置 */}
                  {possibleMove && !piece && (
                    <circle
                      cx={coords.x}
                      cy={coords.y}
                      r={windowSize.width < 640 ? 6 : 8}
                      fill="#32CD32"
                      className="animate-pulse cursor-pointer hover:fill-green-400"
                      onClick={() => {
                        if (!isAIThinking) {
                          onSelectPosition(rowIndex, colIndex);
                        }
                      }}
                    />
                  )}

                  {/* 国王位置高亮 */}
                  {kingPos && !selected && (
                    <circle
                      cx={coords.x}
                      cy={coords.y}
                      r={pieceSize + 5}
                      fill="none"
                      stroke="#FF6347"
                      strokeWidth={2}
                      strokeDasharray="4,2"
                      className="animate-pulse"
                    />
                  )}

                  {/* 透明点击区域 - 用于空格子 */}
                  {!piece && !possibleMove && (
                    <circle
                      cx={coords.x}
                      cy={coords.y}
                      r={25}
                      fill="transparent"
                      className="cursor-pointer"
                      onClick={() => {
                        if (!isAIThinking) {
                          onSelectPosition(rowIndex, colIndex);
                        }
                      }}
                    />
                  )}

                  {/* 棋子 */}
                  {piece && (
                    <g
                      className={`cursor-pointer hover:opacity-85 transition-opacity ${!isAIThinking ? '' : 'cursor-not-allowed'}`}
                      onClick={() => {
                        if (!isAIThinking) {
                          onSelectPosition(rowIndex, colIndex);
                        }
                      }}
                    >
                      {/* 棋子阴影 */}
                      <circle
                        cx={coords.x + shadowSize}
                        cy={coords.y + shadowSize}
                        r={pieceSize}
                        fill="#000000"
                        opacity={0.3}
                      />

                      {/* 棋子主体 */}
                      {piece.type === 'king' ? (
                        // 国王 - 特殊样式
                        <g>
                          <circle
                            cx={coords.x}
                            cy={coords.y}
                            r={pieceSize + 1}
                            fill={piece.player === 'defender' ? '#FFD700' : '#FF6B6B'}
                            stroke={piece.player === 'defender' ? '#DAA520' : '#CC0000'}
                            strokeWidth={windowSize.width < 640 ? 4 : 2}
                          />
                          <text
                            x={coords.x}
                            y={coords.y + textSize * 0.4}
                            textAnchor="middle"
                            fontSize={textSize + 5}
                            fontWeight="bold"
                          >
                            👑
                          </text>
                        </g>
                      ) : piece.type === 'guard' ? (
                        // 守卫
                        <g>
                          <circle
                            cx={coords.x}
                            cy={coords.y}
                            r={pieceSize}
                            fill={piece.player === 'defender' ? '#4169E1' : '#87CEEB'}
                            stroke={piece.player === 'defender' ? '#1E3A8A' : '#4682B4'}
                            strokeWidth={windowSize.width < 640 ? 1 : 2}
                          />
                          <text
                            x={coords.x}
                            y={coords.y + textSize * 0.4}
                            textAnchor="middle"
                            fontSize={textSize}
                            fontWeight="bold"
                            fill="white"
                          >
                            🛡️
                          </text>
                        </g>
                      ) : (
                        // 攻击者
                        <g>
                          <circle
                            cx={coords.x}
                            cy={coords.y}
                            r={pieceSize}
                            fill={piece.player === 'attacker' ? '#DC143C' : '#FF69B4'}
                            stroke={piece.player === 'attacker' ? '#8B0000' : '#C71585'}
                            strokeWidth={windowSize.width < 640 ? 1 : 2}
                          />
                          <text
                            x={coords.x}
                            y={coords.y + textSize * 0.4}
                            textAnchor="middle"
                            fontSize={textSize}
                            fontWeight="bold"
                            fill="white"
                          >
                            ⚔️
                          </text>
                        </g>
                      )}
                    </g>
                  )}
                </g>
              );
            })
          )}
        </svg>
      </div>
    </div>
  );
};

export default TablutBoard;